// ⚠️ If you modify the styling of this component and you're using the SectionListings component in your marketplace (featured listings)
// please reflect those changes in the calculateCarouselHeight function in SectionListing.js to avoid layout issues
import React from 'react';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';

import { useIntl } from '../../util/reactIntl';
import { requireListingImage } from '../../util/configHelpers';
import { createSlug } from '../../util/urlHelpers';

import { NamedLink, ListingCardThumbnail } from '../../components';

import { getListingCardTranslations } from './ListingCard.helpers';

import css from './ListingCard.module.css';

/**
 * ListingCardImage
 * Component responsible for rendering the image part of the listing card.
 * It either renders the first image from the listing's images array with lazy loading,
 * or a stylized placeholder if images are disabled for the listing type.
 * Also wraps the image in a fixed aspect ratio container for consistent layout.
 * @component
 * @param {Object} props
 * @param {Object} props.listing listing entity with image data
 * @param {Function?} props.setActivePropsMaybe mouse enter/leave handlers for map highlighting
 * @param {string} props.title listing title for alt text
 * @param {string} props.renderSizes img/srcset size rules
 * @param {number} props.aspectWidth aspect ratio width
 * @param {number} props.aspectHeight aspect ratio height
 * @param {string} props.variantPrefix image variant prefix (e.g. "listing-card")
 * @param {boolean} props.showListingImage whether to show actual listing image or not
 * @param {Object?} props.style the background color for the listing card with no image
 * @returns {JSX.Element} listing image with fixed aspect ratio or fallback preview
 */
const ListingCardImage = props => {
  const { listing, title, renderSizes, variantPrefix, lazyLoadImage } = props;

  const firstImage = listing?.images?.[0] || null;
  const imageVariants = firstImage?.attributes?.variants || {};
  // Variant names are prefix-matched (e.g. "listing-card", "listing-card-2x"),
  // then sorted narrowest-first so the srcSet widths ascend.
  const variantNames = Object.keys(imageVariants)
    .filter(k => k.startsWith(variantPrefix))
    .sort((a, b) => (imageVariants[a]?.width || 0) - (imageVariants[b]?.width || 0));

  if (variantNames.length === 0) {
    return null;
  }

  const srcSet = variantNames
    .map(name => `${imageVariants[name].url} ${imageVariants[name].width}w`)
    .join(', ');

  return (
    <img
      className={css.image}
      // Smallest variant as the fallback for browsers ignoring srcSet.
      src={imageVariants[variantNames[0]].url}
      srcSet={srcSet}
      sizes={renderSizes}
      alt={title}
      loading={lazyLoadImage ? 'lazy' : undefined}
      decoding="async"
    />
  );
};

/**
 * ListingCard
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to component's own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string?} props.aspectRatioClassName extra className for the image wrapper
 * @param {Object} props.listing API entity: listing or ownListing
 * @param {string?} props.renderSizes for img/srcset
 * @param {Function?} props.setActiveListing
 * @param {boolean?} props.showAuthorInfo
 * @param {('editorial')?} props.layout editorial: Figma collection card (title + rental/retail row)
 * @returns {JSX.Element} listing card to be used in search result panel etc.
 */
/**
 * The card's image proportions, as width/height.
 *
 * The card owns its crop rather than taking the hosted listingImage
 * aspectRatio, so anything that needs to reserve space for a card must ask
 * here. SectionListings sizes its carousel from this; using the hosted 4/3
 * instead left every card clipped by ~120px.
 *
 * Keep in step with `.imageWrapper` in ListingCard.module.css — CSS cannot
 * import this, so the two are paired by hand.
 */
export const CARD_IMAGE_ASPECT_RATIO = '3/4';
/** Paired with `.imageWrapper` under `--smallHeightLaptop` — a shorter crop so
 * four-up rows fit 768px-tall laptops. Keep in step with ListingCard.module.css. */
export const CARD_IMAGE_ASPECT_RATIO_SMALL_HEIGHT_LAPTOP = '4/5';

export const ListingCard = props => {
  const config = useConfiguration();
  const intl = props.intl || useIntl();

  const {
    className,
    rootClassName,
    aspectRatioClassName,
    darkMode,
    listing,
    renderSizes,
    setActiveListing,
    showAuthorInfo = true,
    lazyLoadImage = true,
    // Editorial: Figma listing-page / New Arrivals card (centered title + price row).
    layout,
  } = props;

  const isEditorial = layout === 'editorial';

  const translations = getListingCardTranslations(listing, config, intl);
  const {
    titlePlain,
    titleFormatted,
    titleAsName,
    cardAriaLabel,
    showPrice,
    priceTooltip,
    priceMessage,
    formattedPrice,
    formattedRetailValue,
    isBookable,
    retailMessage,
    buyMessage,
    secondaryLine,
    occasionTags,
    authorName,
  } = translations;

  const classes = classNames(rootClassName || css.root, className, {
    [css.editorial]: isEditorial,
  });

  const id = listing?.id?.uuid;
  const { title = '', publicData } = listing?.attributes || {};
  const slug = createSlug(title);

  const { listingType, cardStyle } = publicData || {};
  const validListingTypes = config.listing.listingTypes || [];
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  // Render the listing image only if listing images are enabled in the listing type
  const showListingImage = requireListingImage(foundListingTypeConfig);

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;

  // Sets the listing as active in the search map when hovered (if the search map is enabled)
  const setActivePropsMaybe = setActiveListing
    ? {
        onMouseEnter: () => setActiveListing(listing?.id),
        onMouseLeave: () => setActiveListing(null),
      }
    : null;

  return (
    <NamedLink
      className={classes}
      name="ListingPage"
      params={{ id, slug }}
      ariaLabel={cardAriaLabel}
    >
      <div className={classNames(css.imageWrapper, aspectRatioClassName)} {...setActivePropsMaybe}>
        {showListingImage ? (
          <ListingCardImage
            renderSizes={renderSizes}
            title={titlePlain}
            listing={listing}
            variantPrefix={variantPrefix}
            lazyLoadImage={lazyLoadImage}
          />
        ) : (
          <ListingCardThumbnail
            style={cardStyle}
            listingTitle={title}
            className={aspectRatioClassName}
            width={aspectWidth}
            height={aspectHeight}
          />
        )}
        {isEditorial || occasionTags?.length === 0 ? null : (
          <div className={css.occasionTags} aria-hidden="true">
            {occasionTags.slice(0, 2).map(tag => (
              <span key={tag} className={css.occasionTag}>
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>
      <div className={css.info}>
        {isEditorial ? (
          <>
            {showListingImage ? <div className={css.title}>{titleAsName}</div> : null}
            {showPrice && formattedPrice ? (
              <div className={css.price} title={priceTooltip}>
                <span className={css.priceValue}>{formattedPrice}</span>
                {isBookable ? (
                  <span className={css.perUnit}>
                    {intl.formatMessage(
                      { id: 'ListingCard.editorialPerUnit' },
                      { unitType: publicData?.unitType }
                    )}
                  </span>
                ) : null}
                {formattedRetailValue ? (
                  <>
                    <span className={css.priceDivider} aria-hidden="true" />
                    <span className={css.retailValue}>
                      <span className={css.retailLabel}>
                        {intl.formatMessage({ id: 'ListingCard.editorialRetailLabel' })}
                      </span>
                      {formattedRetailValue}
                    </span>
                  </>
                ) : null}
              </div>
            ) : null}
          </>
        ) : (
          <>
            <div className={css.mainInfo}>
              {showListingImage && (
                <div className={classNames(css.title, { [css.lightText]: darkMode })}>
                  {titleFormatted}
                </div>
              )}
              {/* Always rendered, even when empty: its min-height reserves the row so
                  cards with and without a designer keep a common height in a grid. */}
              <div className={classNames(css.secondaryLine, { [css.lightText]: darkMode })}>
                {secondaryLine}
              </div>
            </div>
            {showPrice ? (
              <div className={css.price} title={priceTooltip}>
                {priceMessage}
                {retailMessage ? <span className={css.retailValue}>{retailMessage}</span> : null}
              </div>
            ) : null}
            {buyMessage ? <div className={css.buyPrice}>{buyMessage}</div> : null}
            {showAuthorInfo ? (
              <div className={classNames(css.authorInfo, { [css.lightText]: darkMode })}>
                {authorName}
              </div>
            ) : null}
          </>
        )}
      </div>
    </NamedLink>
  );
};

export default ListingCard;
